import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core'
import {
  filterWebsiteTemplate,
  tooltipsForTemplate,
  Website,
  WebsiteTemplateNames,
  isTokeetWebsiteDomain,
  WebsiteTypes,
} from '@tv3/store/website/website.model'
import { select, Store } from '@ngrx/store'
import { isUpdatingWebsite, selectAllWebsiteTemplates } from '@tv3/store/website/website.selectors'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import * as fromRoot from '@tv3/store/state'
import {
  ConfirmDialogService,
  Destroyable,
  Rental,
  RentalService,
  SaveForm,
  selectAllRentals,
  untilDestroy,
  GroupRateService,
  Rate,
} from '@tokeet-frontend/tv3-platform'
import {
  ToggleWebsiteOnlineBookings,
  UpdateWebsite,
  UpdateWebsiteRentalsComplete,
} from '@tv3/store/website/website.actions'
import { WebsiteService } from '@tv3/store/website/website.service'
import { WebsiteTemplate } from '@tv3/store/website/website.types'
import * as R from 'ramda'
import { startCase } from 'lodash'
import { Actions, ofType } from '@ngrx/effects'
import { catchError, delay, distinctUntilChanged, map, startWith, switchMap, tap } from 'rxjs/operators'
import { ConfigWebsiteDomainDialogService } from '@tv3/containers/websites/dialogs/config-website-domain-dialog/config-website-domain-dialog.service'
import { BehaviorSubject, combineLatest, forkJoin, of } from 'rxjs'
import { RentalRateService } from '@tv3/services/rental-rate.service'
import { normalizeLogoRedirectUrl } from '../logo-redirect-url'

@Component({
  selector: 'app-website-details-web',
  templateUrl: './website-details-web.component.html',
  styleUrls: ['./website-details-web.component.scss'],
})
export class WebsiteDetailsWebComponent extends Destroyable implements OnInit, OnChanges {
  @Input() website: Website
  templateNames = WebsiteTemplateNames

  isUpdating$ = this.store.pipe(select(isUpdatingWebsite))

  tooltipsForTemplate = tooltipsForTemplate
  templates: WebsiteTemplate[] = []
  rentals: Rental[] = []
  rateCategories: { value: string; label: string }[] = []
  private rentalIds$ = new BehaviorSubject<string[]>([])
  private rentalRateCategoriesByRental: Record<string, { id: string; name: string }[]> = {}

  form = this.fb.group({
    title: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(50)]],
    description: [undefined, [Validators.maxLength(1000)]],
    domain: [{ value: undefined, disabled: true }],
    rateCategory: [undefined, [Validators.required]],
    template: [undefined, [Validators.required]],
    engine_enabled: [0],
    logoRedirectUrl: [
      '',
      [
        (control) =>
          !control.value ||
          (typeof control.value === 'string' && !control.value.trim()) ||
          normalizeLogoRedirectUrl(control.value)
            ? null
            : { logoRedirectUrl: true },
      ],
    ],
  })

  isCustomDomain = false

  constructor(
    private fb: FormBuilder,
    private store: Store<fromRoot.State>,
    private actions: Actions,
    private websiteService: WebsiteService,
    private groupRateService: GroupRateService,
    private rentalRateService: RentalRateService,
    private confirmDialog: ConfirmDialogService,
    private rentalService: RentalService,
    private configWebsiteDomainDialog: ConfigWebsiteDomainDialogService
  ) {
    super()
  }

  ngOnInit() {
    const rentalRateCategories$ = this.rentalIds$.pipe(
      distinctUntilChanged((previous, current) => R.equals(previous, current)),
      switchMap((rentalIds) => this.loadRentalRateCategories(rentalIds))
    )

    combineLatest([
      this.form.valueChanges.pipe(
        startWith(this.form.getRawValue()),
        switchMap(() => this.store.pipe(select(selectAllWebsiteTemplates))),
        map((templates) => filterWebsiteTemplate(templates, this.website.type, this.website.rentals.length)),
        tap((templates) => (this.templates = templates)),
        tap((templates) => {
          const template = R.find((t) => this.form.get('template').value === t.name, templates)
          if (!template) {
            this.form.patchValue({ template: undefined }, { emitEvent: false })
          }
        })
      ),
      this.store.pipe(
        select(selectAllRentals),
        tap((rentals) => (this.rentals = rentals))
      ),
      this.groupRateService.all(),
      rentalRateCategories$,
    ])
      .pipe(untilDestroy(this))
      .subscribe((data) => {
        this.rateCategories = this.getRentalRateCategories(this.website, this.rentals, data[2], data[3])
      })

    this.form
      .get('engine_enabled')
      .valueChanges.pipe(untilDestroy(this))
      .subscribe((status) => {
        this.store.dispatch(ToggleWebsiteOnlineBookings({ websiteId: this.website.id, status }))
      })

    this.actions
      .pipe(
        ofType(UpdateWebsiteRentalsComplete),
        delay(500), // after website updated
        untilDestroy(this)
      )
      .subscribe(() => {
        this.form.patchValue({ rateCategory: 'default' })
      })
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['website']) {
      this.setFormData(this.website)
      this.rentalIds$.next([...(this.website.rentals || [])])
    }
  }

  private loadRentalRateCategories(rentalIds: string[]) {
    const missingRentalIds = rentalIds.filter((rentalId) => !this.rentalRateCategoriesByRental[rentalId])
    const requests = missingRentalIds.map((rentalId) =>
      this.rentalRateService.getRateCategories(rentalId, false).pipe(
        tap((categories) => (this.rentalRateCategoriesByRental[rentalId] = categories)),
        catchError(() => of([]))
      )
    )

    return (requests.length ? forkJoin(requests) : of([])).pipe(
      map(() =>
        rentalIds.reduce(
          (categories, rentalId) => categories.concat(this.rentalRateCategoriesByRental[rentalId] || []),
          [] as { id: string; name: string }[]
        )
      )
    )
  }

  private getRentalRateCategories(
    website: Website,
    rentals: Rental[],
    groupRates: Rate[],
    rentalRateCategories: { id: string; name: string }[]
  ) {
    const activeRentalRates = rentals
      .filter((rental) => website.rentals.includes(rental.id))
      .reduce((rates, rental) => rates.concat(rental.rates || []), [] as Rate[])

    const rentalIds = R.groupBy((rentalId) => rentalId, website.rentals)
    const activeGroupRates = (groupRates || []).filter((groupRate) => {
      return R.filter((rentalId) => !!rentalIds[rentalId], groupRate.rentalIds).length === website.rentals.length
    })

    const rateCategories = [
      ...rentalRateCategories.map((category) => category.id),
      ...this.rentalService.getUniqueCategories(activeRentalRates),
      ...this.rentalService.getUniqueCategories(activeGroupRates),
    ]

    if (!rateCategories.includes('default')) rateCategories.push('default') // always add 'default'

    const uniqueRateCategories = R.uniq(rateCategories)
    const sortedRateCategories = R.sortBy((category: string) => category.toLowerCase(), uniqueRateCategories)

    return R.map((category) => ({ value: category, label: startCase(category) }), sortedRateCategories)
  }

  private setFormData(website: Website) {
    this.isCustomDomain = !isTokeetWebsiteDomain(website.domain, WebsiteTypes.RentalWebsite)
    this.form.patchValue(
      {
        ...website,
        rateCategory: website.rateCategory,
        engine_enabled: !!website.engineEnabled, // 0|1=>true|false
        template: website.template,
        logoRedirectUrl: website.templateSettings?.logoRedirectUrl || '',
      },
      { emitEvent: false }
    )
  }

  onEditDomain() {
    this.configWebsiteDomainDialog.open(this.website)
  }

  @SaveForm()
  onSave(fb: FormGroup) {
    const formData = fb.getRawValue()
    const template = R.find((t) => formData.template === t.name, this.templates)
    const payload = {
      ...this.website.serialize(),
      title: formData.title,
      description: formData.description,
      rateCategory: formData.rateCategory,
      template: template.name,
      template_type: template.properties,
      template_settings: {
        ...this.website.templateSettings,
        logoRedirectUrl: normalizeLogoRedirectUrl(formData.logoRedirectUrl),
      },
    }
    this.store.dispatch(UpdateWebsite({ websiteId: this.website.id, data: payload }))
  }
}
